<template>
  <i
    class="basic-header-menu el-icon-s-operation"
    @click.self="onClickOperationIcon"
  >
    <nav v-if="navVisible" class="basic-header-menu__nav">
      <a class="nav__logo" :href="homeURL" />

      <ol class="nav__section-title-list">
        <li
          v-for="{ id, title } in sessionTitleList"
          :key="id"
          :class="{ active: sessionTitleListItemActiveClassName(id) }"
          class="list-item"
          @click="onClickTitleListItem(id)"
        >
          <div class="list-item__title-wrapper">
            <i class="list-item__icon el-icon-chat-line-round"></i>
            <p class="list-item__title">{{ title }}</p>
          </div>

          <div class="list-item__button-wrapper">
            <i
              class="list-item__icon el-icon-delete"
              @click="onClickDeleteListItem(id)"
            ></i>
          </div>
        </li>
      </ol>

      <ul class="nav__function-list">
        <li
          v-if="!organized || signInMethod !== 'organizational'"
          class="list-item"
          @click="onClickRechargeListItem"
        >
          <i class="list-item__icon el-icon-wallet"></i>
          <p>充值</p>
        </li>
        <li class="list-item" @click="onClickServiceListItem">
          <i class="list-item__icon el-icon-service"></i>
          <p>联系客服</p>
        </li>
        <li v-if="signedIn" class="list-item" @click="onClickSignOutListItem">
          <i class="list-item__icon el-icon-back"></i>
          <p>退出</p>
        </li>
      </ul>

      <div class="nav__user-info" @click="onClickUserInfo">
        <div
          class="user-info__logo"
          :style="{ backgroundImage: profileBackgroundImageSource }"
        ></div>
        <div class="user-info__text-container">
          <p class="text-container__user-name">
            {{ formatUsername }}
          </p>
          <template v-if="signedIn">
            <template v-if="organized && signInMethod === 'organizational'">
              <p class="text-container__organization-name">
                {{ organizationName }}
              </p>
              <p class="text-container__balance-points">
                今日机构剩余
                <el-tooltip class="item" effect="light" placement="top">
                  <template #content>
                    <p>ChatGPT-3.5和文心一言：提问1点/次</p>
                    <p>ChatGPT-4：提问30点/次</p>
                  </template>
                  <i class="el-icon-info"></i>
                </el-tooltip>
                ：{{ formatOrganizationalBalancePoints || 0 }}
                点
              </p>
            </template>
            <template v-else-if="!organized || signInMethod === 'individual'">
              <p class="text-container__balance-points">
                账户剩余
                <el-tooltip class="item" effect="light" placement="top">
                  <template #content>
                    <p>ChatGPT-3.5和文心一言：提问1点/次</p>
                    <p>ChatGPT-4：提问30点/次</p>
                  </template>
                  <i class="el-icon-info"></i>
                </el-tooltip>
                ：{{ formatUserBalancePoints || 0 }} 点
              </p>
              <p class="text-container__valid-until">
                {{ formatExpiryTime }}
              </p>
            </template>
          </template>
        </div>
      </div>
      <div v-if="!signedIn" class="nav__token-cost-tips">
        <p>ChatGPT-3.5和文心一言：提问1点/次</p>
        <p>ChatGPT-4：提问30点/次</p>
      </div>
      <div
        v-if="signInMethodToggleButtonVisible"
        class="nav__toggle-sign-in-method-button"
      >
        <el-button type="primary" @click="onClickSignInMethodToggleButton">{{
          formatSignInMethodToggleButtonText
        }}</el-button>
      </div>
    </nav>
  </i>
</template>

<script>
import { mapState, mapMutations, mapActions } from "vuex";
import dayjs from "dayjs";
import imageProfilePlain from "@/assets/images/profile-plain.png";
import apiSession from "@/api/session.js";

export default {
  name: "BasicHeaderMenu",
  data() {
    return {
      navVisible: false,
      imageProfilePlain,
    };
  },
  computed: {
    ...mapState(["homeURL"]),
    ...mapState("user", [
      "signedIn",
      "organized",
      "signInMethod",
      "username",
      "userTotalPoints",
      "userUsedPoints",
      "expiryTime",
      "profileImageSource",
      "organizationName",
      "organizationalTotalPoints",
      "organizationalUsedPoints",
    ]),
    ...mapState("session", ["sessionTitleList"]),

    profileBackgroundImageSource() {
      const { signedIn, profileImageSource } = this;
      return `url("${
        signedIn ? profileImageSource || imageProfilePlain : imageProfilePlain
      }")`;
    },
    formatUsername() {
      const { signedIn, username } = this;
      return signedIn ? username : "登录/注册";
    },
    formatOrganizationalBalancePoints() {
      const { organizationalTotalPoints, organizationalUsedPoints } = this;
      return organizationalTotalPoints - organizationalUsedPoints;
    },
    formatUserBalancePoints() {
      const { userTotalPoints, userUsedPoints } = this;
      return userTotalPoints - userUsedPoints;
    },
    formatExpiryTime() {
      const { expiryTime } = this;
      return dayjs(expiryTime).format("[有效期至] YYYY年MM月DD日");
    },
    signInMethodToggleButtonVisible() {
      const { signedIn, organized } = this;
      return signedIn && organized;
    },
    formatSignInMethodToggleButtonText() {
      const { signInMethod } = this;
      return signInMethod === "individual"
        ? "切换至机构权限登录"
        : "切换至个人权限登录";
    },
  },
  methods: {
    ...mapMutations(["setCustomerServiceDialogVisible"]),
    ...mapMutations("user", ["setSignedInMethod"]),
    ...mapActions(["openSignInDialog"]),
    ...mapActions("user", ["handleSignOut"]),

    // 删除会话接口
    async deleteSessionTitle(messageId) {
      const { modelType: model } = this;
      const params = { messageId, model };
      return await apiSession.deleteSessionTitle(params);
    },

    // 删除会话接口操作
    async handleDeleteSessionTitle(id) {
      await this.deleteSessionTitle(id).catch((error) => {
        console.error("handleDeleteSessionTitle");
        throw error;
      });
    },

    sessionTitleListItemActiveClassName(id) {
      const { id: currentRouteParamsId } = this.$route.params;
      return currentRouteParamsId === id;
    },

    onClickOperationIcon() {
      const { navVisible } = this;
      this.navVisible = !navVisible;
    },
    onClickTitleListItem(id) {
      const { id: currentRouteParamsId } = this.$route.params;
      if (currentRouteParamsId === id) {
        return;
      }
      const name = "Session";
      const params = { id };
      this.$router.push({ name, params });
    },
    async onClickDeleteListItem(id) {
      await this.handleDeleteSessionTitle(id).catch((error) => {
        console.error("onClickDeleteListItem");
        throw error;
      });
      const { name: currentRouteName } = this.$route;
      const name = "Home";
      if (currentRouteName === name) {
        location.reload();
        return;
      }
      this.$router.push({ name });
    },
    onClickRechargeListItem() {
      const { signedIn } = this;
      if (!signedIn) {
        this.openSignInDialog();
        this.navVisible = false;
        return;
      }
      const { name: currentRouteName } = this.$route;
      const name = "Recharge";
      this.navVisible = false;
      if (currentRouteName == name) {
        return;
      }
      this.$router.push({ name });
    },
    onClickServiceListItem() {
      this.setCustomerServiceDialogVisible(true);
      this.navVisible = false;
    },
    async onClickSignOutListItem() {
      await this.handleSignOut().catch((error) => {
        console.error("onClickSignOutListItem");
        throw error;
      });
    },
    onClickUserInfo() {
      const { signedIn } = this;
      if (!signedIn) {
        this.openSignInDialog();
        return;
      }
    },
    onClickSignInMethodToggleButton() {
      const { signInMethod } = this;
      this.setSignedInMethod(
        signInMethod === "individual" ? "organizational" : "individual"
      );
      const { name: currentRouteName } = this.$route;
      const name = "Home";
      if (currentRouteName === name) {
        location.reload();
        return;
      }
      this.$router.push({ name });
    },
  },
};
</script>

<style lang="scss" scoped>
.basic-header-menu {
  position: relative;

  .basic-header-menu__nav {
    position: absolute;
    z-index: 1;
    top: var(--header-height);
    left: 8px;
    padding: 8px 8px 16px;
    width: 256px;
    max-height: 460px;
    font-size: 14px;
    color: var(--color-basic-white);
    border-radius: 4px;
    background-color: var(--color-basic-black);

    &::before {
      content: "";
      position: absolute;
      top: -4px;
      left: 8px;
      transform: rotate(45deg);
      width: 16px;
      height: 16px;
      background-color: var(--color-basic-black);
    }

    .nav__logo {
      display: block;
      height: 64px;
      background-image: url("~@/assets/images/logo.png");
      background-size: contain;
      background-repeat: no-repeat;
    }

    .list-item__icon {
      margin-right: 8px;
      font-size: 20px;
      width: 20px;
    }

    .nav__section-title-list {
      margin-top: 8px;
      max-height: 160px;
      overflow: auto;
      border-bottom: 1px solid var(--color-level-1-border);

      .list-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-radius: 4px;

        .list-item__icon.el-icon-delete {
          display: none;
        }

        &.active {
          background-color: #343540;

          .list-item__icon.el-icon-delete {
            display: inline-block;
          }
        }

        .list-item__title-wrapper {
          display: flex;
          align-items: center;

          .list-item__title {
            display: -webkit-box;
            width: 160px;
            overflow: hidden;
            height: 40px;
            line-height: 40px;
            text-overflow: ellipsis;
            word-break: break-all;
            -webkit-line-clamp: 1;
            -webkit-box-orient: vertical;
          }
        }

        .list-item__button-wrapper {
          display: flex;
          align-items: center;
        }
      }
    }

    .nav__function-list {
      padding: 8px 0;

      .list-item {
        display: flex;
        align-items: center;
        height: 40px;
        border-radius: 4px;
        cursor: pointer;
      }
    }

    .nav__user-info {
      padding: 4px 0;
      display: flex;
      align-items: center;

      .user-info__logo {
        margin: 0 8px;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background-position: center;
        background-size: contain;
        background-repeat: no-repeat;
      }

      .user-info__text-container {
        line-height: 1.5;

        .text-container__user-name,
        .text-container__organization-name,
        .text-container__balance-points,
        .text-container__valid-until {
          display: -webkit-box;
          width: 180px;
          overflow: hidden;
          text-overflow: ellipsis;
          word-break: break-all;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
        }
      }
    }

    .nav__token-cost-tips {
      margin-top: 8px;
      line-height: 1.5;
    }

    .nav__toggle-sign-in-method-button {
      margin-top: 8px;
      display: flex;
      justify-content: center;
    }
  }
}
</style>